import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import {
  Grid,
  Icon,
  Popup,
  Segment,
  Table
} from 'semantic-ui-react'
import { format } from 'date-fns'
// Local
import { BASE_URL } from '../../config'
import {
  API_URL,
  FORMAT
} from '../../constant'
import { api } from '../../action/api'
import If from '../common/if'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafePopup = Popup as unknown as ComponentType<any>
const SafeTable = Table as unknown as ComponentType<any>
const SafeIcon = Icon as unknown as ComponentType<any>

type ReviewCountData = {
  reviewCountOpen: number
  reviewCountReadyForCdiQa: number
  reviewCountReadyForPhysician: number
  reviewCountReadyForCoder: number
  reviewCountReadyForCoderQa: number
  reviewCountCompleted: number
}

type ReportData = {
  today: ReviewCountData
  yesterday: ReviewCountData
}

/**
 * Reports the total number of reviews in each work list
 */
function TotalReviewCount(): JSX.Element {
  const [
    isCdiWorklistReportLoading,
    setCdiWorklistReportLoading
  ] = useState<boolean>(false)
  const [
    isCoderWorklistReportLoading,
    setCoderWorklistReportLoading
  ] = useState<boolean>(false)
  const [
    reportData,
    setReportData
  ] = useState<ReportData | null>(null)

  const fetchReportData = (): void => {
    const url = API_URL.DASHBOARD_REVIEW_COUNTS
    api.get(url).then(({ data }: { data: ReportData }) => {
      setReportData(data)
    }).catch(() => {
      // Error handled by api.defaultErrorHandler
    })
  }

  useEffect(() => {
    fetchReportData()
  }, [])

  const onClickRefresh = (): void => {
    fetchReportData()
  }

  const onClickCdiWorklistReportOk = (): void => {
    setCdiWorklistReportLoading(false)
  }

  const onClickCoderWorklistReportOk = (): void => {
    setCoderWorklistReportLoading(false)
  }

  const onClickCdiWorklistReportDownload = (): void => {
    setCdiWorklistReportLoading(true)
  }

  const onClickCoderWorklistReportDownload = (): void => {
    setCoderWorklistReportLoading(true)
  }

  const renderRefresh = (): JSX.Element => (
    <SafeGrid columns={ 1 }>
      <Grid.Column textAlign="right">
        <span className="italic">
          <span>{ format(new Date(), FORMAT.TIMESTAMP) }</span>
          <span>&nbsp;</span>
          <span
            className="linkable"
            onClick={ onClickRefresh }
          >
            Refresh
          </span>
        </span>
      </Grid.Column>
    </SafeGrid>
  )

  const renderEmpty = (): JSX.Element => (
    <div>
      <span className="italic">Sorry, no counts yet today</span>
      <div>{ renderRefresh() }</div>
    </div>
  )

  const renderReport = (
    data: ReportData
  ): JSX.Element => {
    const key = (new Date()).getTime()
    const cdiWorklistReportUrl = BASE_URL + API_URL.REPORT_CDI_CLIENT_VIEW_WORKLIST
    const cdiWorklistFileName = 'cdi-worklist.xlsx'
    const coderWorklistReportUrl = BASE_URL + API_URL.REPORT_CODER_WORKLIST
    const coderWorklistFileName = 'coder-worklist.xlsx'
    return (
      <div key={ key }>
        <SafeTable definition fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>CDI Worklist</Table.HeaderCell>
              <Table.HeaderCell>CDI Manager QA</Table.HeaderCell>
              <Table.HeaderCell>Ready for Physician</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Pre-Visit</Table.Cell>
              <Table.Cell>{ data.today.reviewCountOpen }</Table.Cell>
              <Table.Cell>{ data.today.reviewCountReadyForCdiQa }</Table.Cell>
              <Table.Cell>{ data.today.reviewCountReadyForPhysician }</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <span className="bold">End of Prior Day Count</span>
              </Table.Cell>
              <Table.Cell>{ data.yesterday.reviewCountOpen }</Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <span className="bold">Excel Report</span>
              </Table.Cell>
              <Table.Cell>
                <If isTrue={ isCdiWorklistReportLoading }>
                  <span>
                    <span>Generating CDI worklist report...</span>
                    <span>&nbsp;</span>
                    <span
                      className="linkable"
                      onClick={ onClickCdiWorklistReportOk }
                    >
                      Ok
                    </span>
                  </span>
                </If>
                <If isTrue={ !isCdiWorklistReportLoading }>
                  <SafePopup
                    content="Download CDI worklist as an Excel report"
                    mouseEnterDelay={ 500 }
                    trigger={
                      <a
                        href={ cdiWorklistReportUrl }
                        download={ cdiWorklistFileName }
                        onClick={ onClickCdiWorklistReportDownload }
                      >
                        <SafeIcon name="file excel outline" />
                        CDI Worklist Report
                      </a>
                    }
                  />
                </If>
              </Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </SafeTable>
        <SafeTable definition fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Coder Worklist</Table.HeaderCell>
              <Table.HeaderCell>Coder Manager QA</Table.HeaderCell>
              <Table.HeaderCell>Completed</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <span className="bold">Post-Visit</span>
              </Table.Cell>
              <Table.Cell>{ data.today.reviewCountReadyForCoder }</Table.Cell>
              <Table.Cell>{ data.today.reviewCountReadyForCoderQa }</Table.Cell>
              <Table.Cell>{ data.today.reviewCountCompleted }</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <span className="bold">End of Prior Day Count</span>
              </Table.Cell>
              <Table.Cell>{ data.yesterday.reviewCountReadyForCoder }</Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <span className="bold">Excel Report</span>
              </Table.Cell>
              <Table.Cell>
                <If isTrue={ isCoderWorklistReportLoading }>
                  <span>
                    <span>Generating coder worklist report...</span>
                    <span>&nbsp;</span>
                    <span
                      className="linkable"
                      onClick={ onClickCoderWorklistReportOk }
                    >
                      Ok
                    </span>
                  </span>
                </If>
                <If isTrue={ !isCoderWorklistReportLoading }>
                  <SafePopup
                    content="Download Coder worklist as an Excel report"
                    mouseEnterDelay={ 500 }
                    trigger={
                      <a
                        href={ coderWorklistReportUrl }
                        download={ coderWorklistFileName }
                        onClick={ onClickCoderWorklistReportDownload }
                      >
                        <SafeIcon name="file excel outline" />
                        Coder Worklist Report
                      </a>
                    }
                  />
                </If>
              </Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </SafeTable>
        <div>{ renderRefresh() }</div>
      </div>
    )
  }

  const key = (new Date()).getTime()
  return (
    <SafeSegment key={ key }>
      <h2>Worklist</h2>
      <div className="m-1-top">
        How many encounters are in the pre and post visit work lists?
      </div>
      <div className="m-1-top">
        {
          reportData
            ? renderReport(reportData)
            : renderEmpty()
        }
      </div>
    </SafeSegment>
  )
}

export default TotalReviewCount

